import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunks
export const fetchMyTradeIns = createAsyncThunk(
  'tradeIns/fetchMyTradeIns',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.get('/api/trade-ins', {
        params: { user_id: auth.user.id },
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch trade-ins');
    }
  }
);

export const createTradeIn = createAsyncThunk(
  'tradeIns/createTradeIn',
  async ({ items, paymentMethod }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.post('/api/trade-ins',
        {
          user_id: auth.user.id,
          items,
          payment_method: paymentMethod
        },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create trade-in');
    }
  }
);

export const fetchTradeInById = createAsyncThunk(
  'tradeIns/fetchTradeInById',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.get(`/api/trade-ins/${id}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Trade-in not found');
    }
  }
);

export const acceptTradeInOffer = createAsyncThunk(
  'tradeIns/acceptOffer',
  async (tradeInId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.put(`/api/trade-ins/${tradeInId}`,
        { status: 'accepted' },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to accept offer');
    }
  }
);

export const rejectTradeInOffer = createAsyncThunk(
  'tradeIns/rejectOffer',
  async (tradeInId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const response = await axios.put(`/api/trade-ins/${tradeInId}`,
        { status: 'rejected' },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to reject offer');
    }
  }
);

const initialState = {
  tradeIns: [],
  currentTradeIn: null,
  draftItems: [],
  loading: false,
  submitting: false,
  error: null,
};

const tradeInsSlice = createSlice({
  name: 'tradeIns',
  initialState,
  reducers: {
    addDraftItem: (state, action) => {
      state.draftItems.push(action.payload);
    },
    removeDraftItem: (state, action) => {
      state.draftItems = state.draftItems.filter((_, index) => index !== action.payload);
    },
    updateDraftItem: (state, action) => {
      const { index, item } = action.payload;
      state.draftItems[index] = item;
    },
    clearDraftItems: (state) => {
      state.draftItems = [];
    },
    clearTradeInError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch my trade-ins
      .addCase(fetchMyTradeIns.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMyTradeIns.fulfilled, (state, action) => {
        state.loading = false;
        state.tradeIns = action.payload;
      })
      .addCase(fetchMyTradeIns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create trade-in
      .addCase(createTradeIn.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createTradeIn.fulfilled, (state, action) => {
        state.submitting = false;
        state.tradeIns.unshift(action.payload);
        state.draftItems = [];
      })
      .addCase(createTradeIn.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      // Fetch trade-in by ID
      .addCase(fetchTradeInById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTradeInById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTradeIn = action.payload;
      })
      .addCase(fetchTradeInById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Accept offer
      .addCase(acceptTradeInOffer.fulfilled, (state, action) => {
        const index = state.tradeIns.findIndex(ti => ti.id === action.payload.id);
        if (index !== -1) {
          state.tradeIns[index] = action.payload;
        }
        if (state.currentTradeIn?.id === action.payload.id) {
          state.currentTradeIn = action.payload;
        }
      })
      // Reject offer
      .addCase(rejectTradeInOffer.fulfilled, (state, action) => {
        const index = state.tradeIns.findIndex(ti => ti.id === action.payload.id);
        if (index !== -1) {
          state.tradeIns[index] = action.payload;
        }
        if (state.currentTradeIn?.id === action.payload.id) {
          state.currentTradeIn = action.payload;
        }
      });
  },
});

export const {
  addDraftItem,
  removeDraftItem,
  updateDraftItem,
  clearDraftItems,
  clearTradeInError
} = tradeInsSlice.actions;
export default tradeInsSlice.reducer;
